import {
    PaginationArrowLeftIcon,
    PaginationArrowRightIcon,
    PaginationFirstPageIcon,
    PaginationLastPageIcon,
} from "@smartsheet/lodestar-icons";
import { getClientId } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useEffect } from "react";
import { neutralDark10, neutralDark40 } from "../../../constants/colors";
import { useI18n } from "../../../i18n/i18n";
import { Button } from "../../button";
import { LiveRegion, useLiveRegion } from "../../liveregion";
import { TypeRamp } from "../../typography";
import { Container } from "../styled";
import { PaginationProps } from "../types";

type PaginationBaseProps = Omit<PaginationProps, "locale">;

export const PaginationBase = forwardRef<HTMLDivElement, PaginationBaseProps>(
    (
        {
            className,
            clientId,
            entitiesPerPage,
            from,
            includeFirstAndLastPage = false,
            onFirstPageClick,
            onLastPageClick,
            onNextClick,
            onPrevClick,
            total,
            variant = "inline",
            ...rest
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== "production") {
            if (entitiesPerPage < 1) {
                console.warn("Pagination: `entitiesPerPage` cannot be less than 1.");
            }
            if (total < 1) {
                console.warn(`Pagination: \`total\` (${total}) cannot be less than 1.`);
            }
            if (from < 1) {
                console.warn(`Pagination: \`from\` (${from}) cannot be less than 1.`);
            }
            // if (to < 1) {
            //     console.warn(
            //         `Pagination: \`to\` (${to}) cannot be less than 1.`
            //     );
            // }
            // if (to > total) {
            //     console.warn(
            //         `Pagination: \`to\` (${to}) cannot be greater than \`total\`.`
            //     );
            // }
        }

        const { i18n } = useI18n();
        const cannotGoBack = from === 1;
        const cannotGoForward = from + entitiesPerPage > total;
        const to = Math.min(from + entitiesPerPage - 1, total);

        const { getLiveRegionProps, speak } = useLiveRegion();

        useEffect(() => {
            speak(
                i18n._(
                    /* i18n */ {
                        comment:
                            "This string is read to a screen reader user using a live region when the pagination content changes",
                        id: "{from} through {to} of {total}",
                        values: {
                            from,
                            to,
                            total,
                        },
                    }
                )
            );
        }, [speak, from, i18n, to, total]);

        return (
            <Container
                orientation="row"
                space="small"
                clientId={clientId}
                ref={ref}
                variant={variant}
                className={className}
                role="group"
                aria-label={i18n._(
                    /* i18n */ {
                        comment:
                            "This aria label is read to the user when the screen reader focuses on the container of the pagination component",
                        id: "Pagination, showing {from} through {to} of {total} entities",
                        values: {
                            from,
                            to,
                            total,
                        },
                    }
                )}
                {...rest}
            >
                {includeFirstAndLastPage ? (
                    <React.Fragment>
                        <Button
                            appearance="borderless"
                            clientId={getClientId(clientId, "first")}
                            size="medium"
                            onClick={onFirstPageClick}
                            aria-label={i18n._(
                                /* i18n */ {
                                    comment:
                                        "This aria label is read to the user when the screen reader focuses on the first page button",
                                    id: "Go to the first page",
                                }
                            )}
                            isDisabled={cannotGoBack}
                            iconBefore={
                                <PaginationFirstPageIcon color={cannotGoBack ? neutralDark10 : neutralDark40} />
                            }
                        />
                    </React.Fragment>
                ) : null}
                <Button
                    appearance="borderless"
                    clientId={getClientId(clientId, "prev")}
                    size="medium"
                    onClick={onPrevClick}
                    aria-label={i18n._(
                        /* i18n */ {
                            comment:
                                "This aria label is read to the user when the screen reader focuses on the previous button",
                            id: "Show previous {entitiesPerPage} entities",
                            values: {
                                entitiesPerPage,
                            },
                        }
                    )}
                    isDisabled={cannotGoBack}
                    iconBefore={<PaginationArrowLeftIcon color={cannotGoBack ? neutralDark10 : neutralDark40} />}
                />
                <TypeRamp variant="bodyText" aria-hidden={true}>
                    {i18n._(
                        /* i18n */ {
                            comment:
                                "This is the current range of entities that the user is viewing out of a total number of entities",
                            id: "{from}-{to} of {total}",
                            values: {
                                from,
                                to,
                                total,
                            },
                        }
                    )}
                </TypeRamp>
                <Button
                    appearance="borderless"
                    clientId={getClientId(clientId, "next")}
                    size="medium"
                    onClick={onNextClick}
                    aria-label={i18n._(
                        /* i18n */ {
                            comment:
                                "This aria label is read to the user when the screen reader focuses on the next button",
                            id: "Show next {entitiesPerPage} entities",
                            values: {
                                entitiesPerPage,
                            },
                        }
                    )}
                    isDisabled={cannotGoForward}
                    iconBefore={<PaginationArrowRightIcon color={cannotGoForward ? neutralDark10 : neutralDark40} />}
                />
                {includeFirstAndLastPage ? (
                    <React.Fragment>
                        <Button
                            appearance="borderless"
                            clientId={getClientId(clientId, "last")}
                            size="medium"
                            onClick={onLastPageClick}
                            aria-label={i18n._(
                                /* i18n */ {
                                    comment:
                                        "This aria label is read to the user when the screen reader focuses on the last page button",
                                    id: "Go to the last page",
                                }
                            )}
                            isDisabled={cannotGoForward}
                            iconBefore={
                                <PaginationLastPageIcon color={cannotGoForward ? neutralDark10 : neutralDark40} />
                            }
                        />
                    </React.Fragment>
                ) : null}
                <LiveRegion {...getLiveRegionProps()} />
            </Container>
        );
    }
);

PaginationBase.displayName = "PaginationBase";
